import React from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import { tv } from 'tailwind-variants';
import ButtonWithAdjacentDropdown from '@/components/shared/TextControlsToolbar/Button/ButtonWithAdjacentDropdown';
import useSpeechstream from '@/components/shared/TextToSpeechButton/useSpeechStream';
import DropdownButton from '@/components/ui/Dropdown/DropdownButton';
import Spinner from '@/components/common/Spinner';

interface LanguageSelectMenuProps {
  locale: Locale;
  restoreSelection: () => void;
  selectedLanguage: string;
  setLanguage: (language: string) => void;
}

const containerVariants = tv({
  base: [
    'tw-h-96 tw-box-border',
    'tw-overflow-y-scroll',
    'tw-border tw-border-t-2 tw-border-b-0 tw-border-x-0 tw-border-solid tw-border-grey',
  ],
  variants: {
    isLoading: {
      true: 'tw-border-t-0',
    },
  },
});

const LanguageSelectMenu = ({
  locale, selectedLanguage, restoreSelection, setLanguage,
}: LanguageSelectMenuProps) => {
  const { translationLanguages } = useSpeechstream({ idToRead: '', locale });
  const isLoading = translationLanguages().length === 0;

  const handleLanguageChange = (language: string) => {
    restoreSelection();

    Axios.patch(Routes.plato_api_settings_path(), {
      settings: {
        text_to_speech_language: language,
      },
    }).then(() => {
      setLanguage(language);
    });
  };

  return (
    <div>
      {!isLoading && (
        <ButtonWithAdjacentDropdown.Close asChild>
          <DropdownButton
            key={`language-${selectedLanguage}`}
            selected
            checkmarkPlacement="right"
            onClick={() => handleLanguageChange(selectedLanguage)}
          >
            {selectedLanguage}
          </DropdownButton>
        </ButtonWithAdjacentDropdown.Close>
      )}
      <div
        className={containerVariants({ isLoading })}
      >
        {isLoading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-xs">
            <Spinner />
          </div>
        )}
        {translationLanguages().map(language => (
          <ButtonWithAdjacentDropdown.Close
            key={`language-${language}`}
            asChild
          >
            <DropdownButton
              disabled={selectedLanguage?.toLowerCase() === language?.toLowerCase()}
              onClick={() => handleLanguageChange(language)}
            >
              {language}
            </DropdownButton>
          </ButtonWithAdjacentDropdown.Close>
        ))}
      </div>
    </div>
  );
};

export default LanguageSelectMenu;
